import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexReactClient } from "convex/react";
import App from "./App";
import { setConvexClient } from "@/lib/convexClient";
import "./index.css";

// Convex client (singleton). VITE_CONVEX_URL is auto-populated by `npx convex dev`.
// If unset (preview-only build), the provider falls back to an empty client and
// React components that call useQuery will simply suspend.
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL ?? "";
const convex = new ConvexReactClient(CONVEX_URL || "https://placeholder.convex.cloud");

// Hand the singleton to the imperative modules (snapshotSync, telemetry) that
// run outside the React tree and therefore cannot call useConvex(). Only
// register it when a real URL is configured -- those modules treat a null
// client as a hard failure rather than silently no-op'ing, which is what we
// want on a misconfigured deploy.
if (CONVEX_URL) {
  setConvexClient(convex);
} else {
  console.error(
    "[boot] VITE_CONVEX_URL is not set. Persistence and telemetry are disabled.",
  );
}

if (!window.location.hash) {
  window.location.hash = "#/";
}

// Sentry crash reporting. Only initializes if a DSN is provided via build env.
// Without a DSN, Sentry remains a no-op, which keeps dev cheap and avoids
// reporting noise from the sandbox.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE,
  });
}

// Service worker registration for Web Push. We register the SW unconditionally
// when the browser supports it; permission prompting still happens on the
// Settings page when the user opts in.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .catch((err) => console.warn("[sw] registration failed", err));
  });
}

createRoot(document.getElementById("root")!).render(
  CONVEX_URL
    ? (
        <ConvexAuthProvider client={convex}>
          <App />
        </ConvexAuthProvider>
      )
    : <App />
);
